import { Slot, Slottable } from '@radix-ui/react-slot';
import { ComponentProps, ReactNode } from 'react';
import { AppSpinner } from '../AppSpinner/AppSpinner';
import { cn } from '../cn/cn';
import {
  BUTTON_END_ICON_CLASSES,
  BUTTON_LOADING_INDICATOR_CLASSES,
  BUTTON_START_ICON_CLASSES,
  HIDDEN_WHILE_LOADING,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  buttonClasses,
} from './buttonClasses/buttonClasses';

/**
 * `variant` defaults to 'outline'; solid is for the page's main action.
 *
 * `asChild` renders the child (e.g. a `Link`) with the button's classes, so
 * navigation stays a real link. `Slottable` puts the icons inside that child,
 * since a Slot merges into a single element.
 *
 * `loading` disables the button and draws a spinner over the `invisible`
 * label, so the width holds (buttonClasses says why not a transparent colour).
 * A button waiting on an api uses `loading`, not `disabled`.
 *
 * Don't combine `asChild` and `loading`: the Slot would merge into the hiding
 * span. A link waits on nothing anyway.
 *
 * The spinner is always centred and not swappable; no screen has needed more.
 */
export const Button = ({
  variant = 'outline',
  color = 'primary',
  size = 'md',
  startIcon,
  endIcon,
  loading,
  asChild,
  disabled,
  type = 'button',
  className,
  children,
  ...buttonProps
}: {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  asChild?: boolean;
} & ComponentProps<'button'>) => {
  const Root = asChild === true ? Slot : 'button';
  const hiddenWhileLoading = loading === true && HIDDEN_WHILE_LOADING;

  return (
    <Root
      type={asChild === true ? undefined : type}
      disabled={loading === true ? true : disabled}
      className={buttonClasses({ variant, color, size, className })}
      {...buttonProps}
    >
      {startIcon !== undefined && (
        <span
          className={cn(BUTTON_START_ICON_CLASSES[size], hiddenWhileLoading)}
        >
          {startIcon}
        </span>
      )}
      <Slottable>
        {loading === true ? (
          <span className={HIDDEN_WHILE_LOADING}>{children}</span>
        ) : (
          children
        )}
      </Slottable>
      {loading === true && (
        <span className={BUTTON_LOADING_INDICATOR_CLASSES}>
          <AppSpinner size='sm' className='text-current' />
        </span>
      )}
      {endIcon !== undefined && (
        <span className={cn(BUTTON_END_ICON_CLASSES[size], hiddenWhileLoading)}>
          {endIcon}
        </span>
      )}
    </Root>
  );
};
